/**
 * Spotting a new deployment while a tab is open, so moving to another page
 * loads the new build instead of asking for files the deploy replaced (the
 * "Something went wrong" users met after each release).
 *
 * Every build's index.html names its own entry script with a content hash
 * (`/assets/index-Xd0PxMmF.js`). The page compares the one it was loaded with
 * against the live index.html's -- every few minutes, and whenever the tab is
 * looked at again. A different one means a new build is out; the next page
 * change then does a full load (useLoadNewBuildOnNavigate), so nothing the
 * user is in the middle of is interrupted.
 */

const CHECK_EVERY_MS = 5 * 60 * 1000;
// Vite's own entry only (`/assets/index-<hash>.js`): any other module script a
// page might gain (an extension, a third-party tag) must never read as "a new
// build", or every page change would become a reload.
const ENTRY_SCRIPT = /<script[^>]*type="module"[^>]*src="([^"]*\/assets\/index-[^"]+\.js)"/;

let newBuildWaiting = false;

/** The entry script this page was loaded with. */
const loadedEntry = (): string | null =>
  document
    .querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')
    ?.getAttribute("src") ?? null;

const check = async () => {
  if (newBuildWaiting) return;
  const loaded = loadedEntry();
  if (!loaded) return;
  try {
    // no-store: past every cache, to what is deployed right now.
    const response = await fetch(`${import.meta.env.BASE_URL}index.html`, { cache: "no-store" });
    if (!response.ok) return;
    const live = ENTRY_SCRIPT.exec(await response.text())?.[1];
    if (live && live !== loaded) newBuildWaiting = true;
  } catch {
    // Offline or mid-deploy -- try again at the next check.
  }
};

/** Start watching (once, from main.tsx). Deployed builds only: the dev server
 *  replaces modules itself. */
export const watchForNewBuild = () => {
  if (import.meta.env.DEV) return;
  window.setInterval(() => void check(), CHECK_EVERY_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void check();
  });
};

/** True once a newer build than this page's has been deployed. */
export const isNewBuildWaiting = () => newBuildWaiting;
